// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AppConfig } from "../../config/config";
import { INTERNAL_EMAIL_DOMAIN_SUFFIX, SnackMessage, httpStatus } from "../../config/constant";
import { APIService } from "../../utils/apiService";
import { GetContactRequestState, ContactInterface } from "../../utils/types";
import { setStatus } from "../authSlice/authSlice";
import { enqueueSnackbarMessage } from "../commonSlice/common";

const initialState: GetContactRequestState = {
  state: "idle",
  stateMessage: null,
  errorMessage: null,
  response: {
    id: "",
    firstName: "",
    lastName: "",
    email: "",
    certificationId: "",
    certificationLeaderboardConsent: false,
    isInternalUser: true,
  },
  backgroundProcess: false,
  backgroundProcessMessage: null,
};

export const getContact = createAsyncThunk(
  "contact/getContact",
  async (contactEmail: string, { dispatch }) => {
    return new Promise<{ contact: ContactInterface }>((resolve, reject) => {
      APIService.getInstance()
        .get(AppConfig.serviceUrls.getContact + "/?email=" + contactEmail)
        .then((resp) => {
          if (resp.status == 200) {
            dispatch(setStatus("success"));
            resolve({ contact: resp.data });
          } else {
            reject("Something went wrong");
          }
        })
        .catch((error: Error) => {
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.certificationContact,
              type: "error",
            })
          );
          reject(error);
        });
    });
  }
);

export const contactSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    updateContactLeaderboardConsent: (state, action) => {
      state.response.certificationLeaderboardConsent = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getContact.pending, (state, action) => {
        state.stateMessage = "Loading Contact";
        state.state = "loading";
        state.backgroundProcess = true;
        state.backgroundProcessMessage = "Loading...";
      })
      .addCase(getContact.fulfilled, (state, action) => {
        action.payload.contact.isInternalUser = action.payload.contact.email.includes(INTERNAL_EMAIL_DOMAIN_SUFFIX);
        state.response = action.payload.contact;
        state.state = "success";
        state.backgroundProcess = false;
      })
      .addCase(getContact.rejected, (state, action) => {
        state.state = "failed";
        state.backgroundProcess = false;
      });
  },
});

export const { updateContactLeaderboardConsent } = contactSlice.actions;
export default contactSlice.reducer;
